import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Workflow, ChevronRight, ChevronDown, ArrowRightLeft, FlaskConical, Play, CheckCircle2, AlertTriangle,
  Plus, Pencil, Package, Download, Upload,
} from 'lucide-react';

import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { AppRoutes } from '@/constants/routes';
import {
  postingRuleApi,
  type PostingRuleListDto,
  type PostingRuleDetailDto,
  type PostingPreviewResult,
  type SetupPackDto,
  type PackInstallResultDto,
} from '@/api/postingRule.api';
import { sampleFor } from './sampleEvents';
import {
  loadCompanySampleCodes,
  applyCompanyCodes,
  hasAnyCodes,
  EMPTY_CODES,
  type CompanySampleCodes,
} from './sampleContext';

/**
 * The double-entry a preview produced. Shared by the ad-hoc harness at the top of the page and by
 * each rule's own test, so a result reads identically wherever it was run from.
 */
const PreviewResultView = ({ result }: { result: PostingPreviewResult }) => {
  if (!result.matched || result.error) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800/50 dark:bg-amber-900/20 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
        <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
        <span>{result.error ?? 'No matching rule.'}</span>
      </div>
    );
  }
  return (
    <>
      <div className="flex items-center gap-2 text-sm mb-2 flex-wrap">
        <span className="text-slate-500 dark:text-gray-400">Matched</span>
        <code className="font-mono font-semibold text-signal">{result.ruleCode}</code>
        <span className={cn('ml-auto flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold',
          result.balanced
            ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300'
            : 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400')}>
          {result.balanced ? <><CheckCircle2 size={12} /> Balanced</> : <><AlertTriangle size={12} /> Unbalanced</>}
          <span className="ml-1 font-mono">
            Dr {result.totalDebit.toLocaleString()} / Cr {result.totalCredit.toLocaleString()}
          </span>
        </span>
      </div>
      <table className="w-full text-sm">
        <tbody>
          {result.lines.map((l, i) => (
            <tr key={i} className="border-b border-slate-50 dark:border-slate-700/40 last:border-0">
              <td className="py-1.5 w-10">
                <span className={cn('font-semibold text-xs', l.drCr === 'D' ? 'text-blue-600 dark:text-blue-400' : 'text-rose-600 dark:text-rose-400')}>
                  {l.drCr === 'D' ? 'Dr' : 'Cr'}
                </span>
              </td>
              <td className="py-1.5 font-mono text-xs text-slate-500 dark:text-gray-400 w-16">{l.accountCode}</td>
              <td className="py-1.5 text-slate-700 dark:text-slate-200">{l.accountName}</td>
              <td className="py-1.5 text-right font-mono text-slate-800 dark:text-slate-100">
                {l.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

const SAMPLE_EVENT = JSON.stringify({
  eventType: 'pos_sales.daily_summary',
  businessDate: '2026-07-12',
  financialYearCode: 'FY2026',
  financialPeriodCode: '2026-07',
  totals: { taxTotal: 18000, cogsTotal: 50000 },
  revenueByCategory: [{ categoryExternalId: 'GROCERY', net: 100000 }],
  tenders: [
    { modeCode: 'CASH', amount: 60000, mdrRate: 0 },
    { modeCode: 'CARD_VISA', amount: 58000, mdrRate: 0.021 },
  ],
}, null, 2);

const TARGET_STYLE: Record<string, string> = {
  GL_JOURNAL: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
  AP_BILL:    'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  AR_INVOICE: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
};

export const PostingRulesPage = () => {
  const navigate = useNavigate();
  const [rules, setRules]     = useState<PostingRuleListDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId]   = useState<string | null>(null);
  const [detail, setDetail]   = useState<Record<string, PostingRuleDetailDto>>({});
  const [loadingId, setLoadingId] = useState<string | null>(null);

  // Preview / test harness
  const [showTest, setShowTest]   = useState(false);
  const [sample, setSample]       = useState(SAMPLE_EVENT);
  const [preview, setPreview]     = useState<PostingPreviewResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewErr, setPreviewErr] = useState<string | null>(null);

  const runPreview = async () => {
    setPreviewing(true); setPreviewErr(null); setPreview(null);
    try {
      const parsed = JSON.parse(sample);
      setPreview(await postingRuleApi.preview(parsed));
    } catch (e) {
      setPreviewErr(e instanceof SyntaxError ? 'Invalid JSON: ' + e.message : 'Preview failed.');
    } finally { setPreviewing(false); }
  };

  // Per-rule test. Kept beside the rule rather than in the harness at the top of the page: the
  // question being asked is "what does THIS rule do", and an answer that appears somewhere else
  // makes the reader carry the rule in their head while they scroll to it.
  const [testOpen, setTestOpen]     = useState<string | null>(null);
  const [testSample, setTestSample] = useState('');
  const [testResult, setTestResult] = useState<PostingPreviewResult | null>(null);
  const [testBusy, setTestBusy]     = useState(false);
  const [testErr, setTestErr]       = useState<string | null>(null);
  const [testUsing, setTestUsing]   = useState<string[]>([]);

  // This company's mapped external codes, so a sample tests the rule rather than failing on a
  // supplier code that only exists in the documentation. Fetched once, on the first test opened.
  const [codes, setCodes]           = useState<CompanySampleCodes>(EMPTY_CODES);
  const [codesLoaded, setCodesLoaded] = useState(false);

  const buildSample = useCallback((eventType: string, c: CompanySampleCodes) => {
    const shipped = sampleFor(eventType);
    const { text, substituted } = applyCompanyCodes(shipped, c);
    setTestSample(text);
    setTestUsing(substituted);
  }, []);

  const openTest = async (rule: PostingRuleListDto) => {
    if (testOpen === rule.id) { setTestOpen(null); return; }
    setTestOpen(rule.id);
    setTestResult(null);
    setTestErr(null);

    let c = codes;
    if (!codesLoaded) {
      buildSample(rule.sourceEventType, EMPTY_CODES);   // show something immediately
      c = await loadCompanySampleCodes();
      setCodes(c);
      setCodesLoaded(true);
    }
    buildSample(rule.sourceEventType, c);
  };

  const runRuleTest = async () => {
    setTestBusy(true); setTestErr(null); setTestResult(null);
    try {
      setTestResult(await postingRuleApi.preview(JSON.parse(testSample)));
    } catch (e) {
      setTestErr(e instanceof SyntaxError ? 'Invalid JSON: ' + e.message : 'Preview failed.');
    } finally { setTestBusy(false); }
  };

  // Governance actions
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [govBusy, setGovBusy] = useState(false);

  // Setup packs: export this company's rules, import a pack exported from another company,
  // install a registered pack into this one.
  const [showPacks, setShowPacks]     = useState(false);
  const [packs, setPacks]             = useState<SetupPackDto[]>([]);
  const [packsLoaded, setPacksLoaded] = useState(false);
  const [packBusy, setPackBusy]       = useState(false);
  const [packMsg, setPackMsg]         = useState<string | null>(null);
  const [packErr, setPackErr]         = useState<string | null>(null);

  const importFile = useRef<HTMLInputElement>(null);
  const [importOpen, setImportOpen]   = useState(false);
  const [importCode, setImportCode]   = useState('');
  const [importName, setImportName]   = useState('');
  const [importFileName, setImportFileName] = useState<string | null>(null);
  const [importDefinition, setImportDefinition] = useState<string | null>(null);

  const [installCode, setInstallCode] = useState('');
  const [installPreview, setInstallPreview] = useState<PackInstallResultDto | null>(null);

  const loadPacks = useCallback(async () => {
    try { setPacks(await postingRuleApi.listPacks()); setPacksLoaded(true); }
    catch { setPackErr('Could not load setup packs.'); }
  }, []);

  const togglePacks = () => {
    setShowPacks(v => !v);
    if (!packsLoaded) void loadPacks();
  };

  const exportPack = async () => {
    setPackBusy(true); setPackErr(null); setPackMsg(null);
    try {
      const res = await postingRuleApi.exportPack();
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'posting-rules-pack.json'; a.click();
      URL.revokeObjectURL(url);
    } catch {
      setPackErr('Could not export this company’s rules.');
    } finally { setPackBusy(false); }
  };

  const readImportFile = async (file: File) => {
    setImportFileName(file.name);
    setImportDefinition(await file.text());
    if (!importName) setImportName(file.name.replace(/\.[^.]+$/, ''));
  };

  const submitImport = async () => {
    if (!importCode.trim() || !importDefinition) return;
    setPackBusy(true); setPackErr(null); setPackMsg(null);
    try {
      await postingRuleApi.importPack({
        packCode: importCode.trim(), packName: importName.trim() || importCode.trim(),
        description: null, definition: importDefinition,
      });
      setPackMsg(`Pack '${importCode.trim().toUpperCase()}' imported.`);
      setImportOpen(false); setImportCode(''); setImportName(''); setImportFileName(null); setImportDefinition(null);
      await loadPacks();
    } catch (e: any) {
      setPackErr(e?.response?.data?.message ?? 'Import failed.');
    } finally { setPackBusy(false); }
  };

  const previewInstall = async () => {
    if (!installCode) return;
    setPackBusy(true); setPackErr(null); setInstallPreview(null);
    try { setInstallPreview(await postingRuleApi.installPack(installCode, true)); }
    catch (e: any) { setPackErr(e?.response?.data?.message ?? 'Preview failed.'); }
    finally { setPackBusy(false); }
  };

  const confirmInstall = async () => {
    if (!installCode) return;
    setPackBusy(true); setPackErr(null);
    try {
      const result = await postingRuleApi.installPack(installCode, false);
      setInstallPreview(result);
      setPackMsg(`Pack '${installCode}' installed.`);
      await load();
    } catch (e: any) {
      setPackErr(e?.response?.data?.message ?? 'Install failed.');
    } finally { setPackBusy(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { setRules(await postingRuleApi.list()); }
    catch (e) { console.error('Failed to load posting rules', e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const govAction = useCallback(async (id: string, action: 'submit' | 'approve' | 'activate' | 'suspend') => {
    setGovBusy(true);
    try { await postingRuleApi[action](id); await load(); }
    catch (e: any) { alert(e?.response?.data?.message ?? 'Action failed.'); }
    finally { setGovBusy(false); }
  }, [load]);

  const confirmReject = useCallback(async () => {
    if (!rejecting || !rejectReason.trim()) return;
    setGovBusy(true);
    try { await postingRuleApi.reject(rejecting, rejectReason.trim()); setRejecting(null); setRejectReason(''); await load(); }
    catch (e: any) { alert(e?.response?.data?.message ?? 'Reject failed.'); }
    finally { setGovBusy(false); }
  }, [rejecting, rejectReason, load]);

  const toggle = async (id: string) => {
    if (openId === id) { setOpenId(null); return; }
    setOpenId(id);
    if (!detail[id]) {
      setLoadingId(id);
      try {
        const d = await postingRuleApi.get(id);
        if (d) setDetail(prev => ({ ...prev, [id]: d }));
      } finally { setLoadingId(null); }
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <Workflow size={20} className="text-signal" />
            Posting Rules
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            The configuration that decides the double-entry for every posting from myPOS ONE. Accounts are
            resolved from these rules — never hardcoded.
          </p>
        </div>
        <button onClick={() => navigate(AppRoutes.INTEGRATION_POSTING_RULE_NEW)}
          className="px-3.5 py-2 text-sm font-semibold text-white bg-signal hover:bg-signal-light rounded-lg flex items-center gap-1.5 flex-shrink-0">
          <Plus size={15} /> New rule
        </button>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {/* Preview / test harness */}
        <div className="max-w-4xl mb-4">
          <button onClick={() => setShowTest(v => !v)}
            className="flex items-center gap-2 text-sm font-medium text-signal hover:underline">
            <FlaskConical size={15} /> {showTest ? 'Hide test' : 'Test a rule — preview a sample event'}
          </button>
          {showTest && (
            <div className="mt-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 shadow-card">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                Paste a sample event; see the exact double-entry it would post — <strong>nothing is saved</strong>.
              </p>
              <textarea value={sample} onChange={e => setSample(e.target.value)} rows={9} spellCheck={false}
                className="w-full font-mono text-xs rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 p-3 text-slate-800 dark:text-slate-100 outline-none focus:ring-1 focus:ring-signal" />
              <div className="flex items-center gap-3 mt-2">
                <button onClick={runPreview} disabled={previewing}
                  className="px-3.5 py-2 text-sm font-semibold text-white bg-signal hover:bg-signal-light rounded-lg disabled:opacity-50 flex items-center gap-1.5">
                  {previewing ? <><Spinner size="sm" /> Running…</> : <><Play size={14} /> Preview</>}
                </button>
                {previewErr && <span className="text-sm text-rose-600 dark:text-rose-400 flex items-center gap-1"><AlertTriangle size={14} /> {previewErr}</span>}
              </div>

              {preview && <div className="mt-3"><PreviewResultView result={preview} /></div>}
            </div>
          )}
        </div>

        {/* Setup packs: reuse a company's rules in another, instead of hand-building them twice */}
        <div className="max-w-4xl mb-4">
          <button onClick={togglePacks}
            className="flex items-center gap-2 text-sm font-medium text-signal hover:underline">
            <Package size={15} /> {showPacks ? 'Hide setup packs' : 'Setup packs — export, import, install'}
          </button>
          {showPacks && (
            <div className="mt-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 shadow-card space-y-4">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                A pack is this company&rsquo;s rules as a file. Export it to reuse a well-configured
                set of rules in another company; install a pack someone handed you the same way.
              </p>

              {packErr && <div className="text-sm text-rose-600 dark:text-rose-400 flex items-center gap-1"><AlertTriangle size={14} /> {packErr}</div>}
              {packMsg && <div className="text-sm text-teal-600 dark:text-teal-400 flex items-center gap-1"><CheckCircle2 size={14} /> {packMsg}</div>}

              <div className="flex flex-wrap items-center gap-2">
                <button onClick={exportPack} disabled={packBusy}
                  className="px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg disabled:opacity-50 flex items-center gap-1.5">
                  <Download size={13} /> Export this company&rsquo;s rules
                </button>
                <button onClick={() => setImportOpen(v => !v)} disabled={packBusy}
                  className="px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg disabled:opacity-50 flex items-center gap-1.5">
                  <Upload size={13} /> Import a pack
                </button>
              </div>

              {importOpen && (
                <div className="rounded-lg border border-slate-200 dark:border-slate-600 p-3 space-y-2">
                  <div
                    onClick={() => importFile.current?.click()}
                    className="border-2 border-dashed rounded-lg px-4 py-5 text-center cursor-pointer border-slate-300 dark:border-slate-600 hover:border-signal"
                  >
                    <p className="text-xs text-slate-600 dark:text-slate-300">
                      {importFileName ?? 'Click to choose the .json file someone exported'}
                    </p>
                    <input ref={importFile} type="file" accept=".json" className="hidden"
                      onChange={e => { const f = e.target.files?.[0]; if (f) void readImportFile(f); e.target.value = ''; }} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input value={importCode} onChange={e => setImportCode(e.target.value)} placeholder="Pack code, e.g. SALES_V1"
                      className="px-2.5 py-1.5 text-xs rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100" />
                    <input value={importName} onChange={e => setImportName(e.target.value)} placeholder="Pack name"
                      className="px-2.5 py-1.5 text-xs rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100" />
                  </div>
                  <button onClick={submitImport} disabled={packBusy || !importCode.trim() || !importDefinition}
                    className="px-3 py-1.5 text-xs font-semibold text-white bg-signal hover:bg-signal-light rounded-lg disabled:opacity-50">
                    Register pack
                  </button>
                </div>
              )}

              <div className="border-t border-slate-100 dark:border-slate-700/60 pt-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Install a pack here</p>
                {packs.length === 0 ? (
                  <p className="text-xs text-slate-400">{packsLoaded ? 'No posting-rule packs available yet.' : 'Loading…'}</p>
                ) : (
                  <div className="flex items-center gap-2">
                    <select value={installCode} onChange={e => { setInstallCode(e.target.value); setInstallPreview(null); }}
                      className="px-2.5 py-1.5 text-xs rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100">
                      <option value="">— choose a pack —</option>
                      {packs.map(p => (
                        <option key={p.id} value={p.packCode}>
                          {p.packName} ({p.packCode}) · {p.itemCount} rule{p.itemCount === 1 ? '' : 's'}{p.isSystem ? ' · shipped' : ''}
                        </option>
                      ))}
                    </select>
                    <button onClick={previewInstall} disabled={packBusy || !installCode}
                      className="px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 rounded-lg disabled:opacity-50">
                      Preview
                    </button>
                  </div>
                )}

                {installPreview && (
                  <div className="mt-3 space-y-2">
                    <table className="w-full text-xs">
                      <tbody>
                        {installPreview.items.map(i => (
                          <tr key={i.ruleCode} className="border-b border-slate-50 dark:border-slate-700/40 last:border-0">
                            <td className="py-1 pr-2 font-mono text-slate-600 dark:text-slate-300">{i.ruleCode}</td>
                            <td className="py-1 pr-2 text-slate-500 dark:text-slate-400">{i.description}</td>
                            <td className="py-1">
                              <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-semibold',
                                i.action === 'CREATE' ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300'
                                  : i.action === 'UPDATE' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                                    : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400')}>
                                {i.action}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {installPreview.warnings.map((w, i) => (
                      <div key={i} className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                        <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" /> {w}
                      </div>
                    ))}
                    <button onClick={confirmInstall} disabled={packBusy}
                      className="px-3 py-1.5 text-xs font-semibold text-white bg-signal hover:bg-signal-light rounded-lg disabled:opacity-50">
                      Confirm install
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Spinner size="lg" /></div>
        ) : rules.length === 0 ? (
          <div className="text-center py-16 rounded-xl border border-dashed border-slate-300 dark:border-slate-700">
            <Workflow size={32} className="mx-auto text-slate-300 dark:text-slate-600" />
            <p className="mt-3 text-sm font-medium text-slate-600 dark:text-slate-300">No posting rules configured</p>
          </div>
        ) : (
          <div className="max-w-4xl space-y-2">
            {rules.map(r => {
              const open = openId === r.id;
              const d = detail[r.id];
              return (
                <div key={r.id} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-card overflow-hidden">
                  <button onClick={() => toggle(r.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors">
                    {open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                    <code className="text-xs font-mono text-signal font-semibold">{r.sourceEventType}</code>
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-100">{r.ruleName}</span>
                    <span className={cn('ml-auto px-2 py-0.5 rounded text-xs font-semibold', TARGET_STYLE[r.targetDocumentType] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400')}>
                      {r.targetDocumentType}
                    </span>
                    <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium',
                      r.governanceStatus === 'Active' ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300'
                        : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300')}>
                      {r.governanceStatus} · v{r.versionNo}
                    </span>
                  </button>

                  {open && (
                    <div className="border-t border-slate-100 dark:border-slate-700/60 px-4 py-3">
                      {loadingId === r.id || !d ? (
                        <div className="flex items-center justify-center py-6"><Spinner size="sm" /></div>
                      ) : (
                        <>
                          {d.conditions.length > 0 && (
                            <div className="mb-3">
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">When</p>
                              <div className="flex flex-wrap gap-1.5">
                                {d.conditions.map((c, i) => (
                                  <span key={i} className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-xs font-mono text-slate-600 dark:text-slate-300">
                                    {c.conditionExpression ?? `${c.attribute} ${c.operator} ${c.value ?? ''}`}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1 flex items-center gap-1">
                            <ArrowRightLeft size={12} /> Post these lines
                          </p>
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-xs text-slate-400 border-b border-slate-100 dark:border-slate-700/60">
                                <th className="py-1.5 pr-2 font-semibold w-14">Dr/Cr</th>
                                <th className="py-1.5 pr-2 font-semibold">Amount</th>
                                <th className="py-1.5 pr-2 font-semibold">Account (source → role)</th>
                                <th className="py-1.5 font-semibold">For each</th>
                              </tr>
                            </thead>
                            <tbody>
                              {d.lines.map(l => (
                                <tr key={l.lineNumber} className="border-b border-slate-50 dark:border-slate-700/40 last:border-0">
                                  <td className="py-1.5 pr-2">
                                    <span className={cn('font-semibold text-xs',
                                      l.drCr === 'D' ? 'text-blue-600 dark:text-blue-400'
                                        : l.drCr === 'C' ? 'text-rose-600 dark:text-rose-400' : 'text-slate-500 dark:text-gray-400')}>
                                      {l.drCr === 'D' ? 'Dr' : l.drCr === 'C' ? 'Cr' : 'Bal'}
                                    </span>
                                  </td>
                                  <td className="py-1.5 pr-2 font-mono text-xs text-slate-700 dark:text-slate-300">
                                    {l.amountBasis}
                                    {l.includeWhen && <span className="text-slate-400"> · if {l.includeWhen}</span>}
                                  </td>
                                  <td className="py-1.5 pr-2 text-xs text-slate-600 dark:text-slate-300">
                                    <span className="font-mono">{l.accountSource}</span>
                                    <span className="text-slate-400"> → </span>
                                    <span className="font-semibold">{l.accountRole || l.accountRef}</span>
                                  </td>
                                  <td className="py-1.5 text-xs text-slate-400 font-mono">{l.iterator ?? '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {/* Test this rule against a sample event */}
                          <div className="mt-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/30">
                            <button onClick={() => openTest(r)}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-signal hover:underline">
                              <FlaskConical size={14} />
                              {testOpen === r.id ? 'Hide test' : 'Test this rule — preview a sample event'}
                            </button>

                            {testOpen === r.id && (
                              <div className="px-3 pb-3">
                                <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                                  A sample <code className="font-mono">{r.sourceEventType}</code> event, from the
                                  integration guide.{' '}
                                  {testUsing.length > 0 ? (
                                    <>Using this company&rsquo;s mapped {testUsing.join(', ')} so it resolves.</>
                                  ) : codesLoaded && !hasAnyCodes(codes) ? (
                                    <>No master data is mapped in this company yet, so the guide&rsquo;s illustrative
                                    codes are used — anything resolved through a mapping will report as unmapped.</>
                                  ) : (
                                    <>Edit any code to match this company.</>
                                  )}{' '}
                                  <strong>Nothing is saved.</strong>
                                </p>
                                {r.targetDocumentType !== 'GL_JOURNAL' && (
                                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                                    This rule targets <strong>{r.targetDocumentType}</strong>, which also opens a
                                    subledger document. Preview renders the GL journal only, so it will confirm the
                                    rule matches and where it fails — but it will not list the posting lines.
                                  </p>
                                )}
                                <textarea value={testSample} onChange={e => setTestSample(e.target.value)} rows={10} spellCheck={false}
                                  className="w-full font-mono text-xs rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 p-3 text-slate-800 dark:text-slate-100 outline-none focus:ring-1 focus:ring-signal" />
                                <div className="flex items-center gap-3 mt-2">
                                  <button onClick={runRuleTest} disabled={testBusy}
                                    className="px-3.5 py-2 text-sm font-semibold text-white bg-signal hover:bg-signal-light rounded-lg disabled:opacity-50 flex items-center gap-1.5">
                                    {testBusy ? <><Spinner size="sm" /> Running…</> : <><Play size={14} /> Preview</>}
                                  </button>
                                  <button onClick={() => buildSample(r.sourceEventType, codes)}
                                    className="text-xs font-medium text-slate-500 dark:text-slate-400 hover:underline">
                                    Reset to sample
                                  </button>
                                  {testErr && (
                                    <span className="text-sm text-rose-600 dark:text-rose-400 flex items-center gap-1">
                                      <AlertTriangle size={14} /> {testErr}
                                    </span>
                                  )}
                                </div>
                                {testResult && <div className="mt-3"><PreviewResultView result={testResult} /></div>}
                              </div>
                            )}
                          </div>

                          <div className="mt-3 flex items-center justify-end gap-2 flex-wrap">
                            {/* Governance lifecycle actions (maker-checker) */}
                            {(r.governanceStatus === 'Draft' || r.governanceStatus === 'Rejected') && (
                              <button disabled={govBusy} onClick={() => govAction(r.id, 'submit')}
                                className="text-xs font-medium px-2 py-1 rounded bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 disabled:opacity-40">Submit for approval</button>
                            )}
                            {r.governanceStatus === 'Submitted' && (<>
                              <button disabled={govBusy} onClick={() => govAction(r.id, 'approve')}
                                className="text-xs font-medium px-2 py-1 rounded bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 hover:bg-teal-200 disabled:opacity-40 flex items-center gap-1"><CheckCircle2 size={12} /> Approve</button>
                              <button disabled={govBusy} onClick={() => { setRejecting(r.id); setRejectReason(''); }}
                                className="text-xs font-medium px-2 py-1 rounded bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 disabled:opacity-40">Reject</button>
                            </>)}
                            {r.governanceStatus === 'Approved' && (
                              <button disabled={govBusy} onClick={() => govAction(r.id, 'activate')}
                                className="text-xs font-semibold px-2 py-1 rounded bg-teal-600 text-white hover:bg-teal-500 disabled:opacity-40 flex items-center gap-1"><Play size={11} /> Activate</button>
                            )}
                            {r.governanceStatus === 'Active' && (
                              <button disabled={govBusy} onClick={() => govAction(r.id, 'suspend')}
                                className="text-xs font-medium px-2 py-1 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 hover:bg-amber-200 disabled:opacity-40">Suspend</button>
                            )}
                            {r.governanceStatus === 'Suspended' && (
                              <button disabled={govBusy} onClick={() => govAction(r.id, 'activate')}
                                className="text-xs font-medium px-2 py-1 rounded bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 hover:bg-teal-200 disabled:opacity-40 flex items-center gap-1"><Play size={11} /> Reactivate</button>
                            )}
                            <button onClick={() => navigate(AppRoutes.INTEGRATION_POSTING_RULE_EDIT.replace(':id', r.id))}
                              className="text-xs font-medium text-signal hover:underline flex items-center gap-1">
                              <Pencil size={12} /> Edit rule
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Reject modal (in-app) */}
      {rejecting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !govBusy && setRejecting(null)}>
          <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-xl shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700">
              <h3 className="font-semibold text-slate-900 dark:text-white">Reject rule</h3>
            </div>
            <div className="p-5">
              <label className="block text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Reason</label>
              <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3} autoFocus
                placeholder="Why is this rule being sent back?"
                className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-signal resize-none" />
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700">
              <button onClick={() => setRejecting(null)} disabled={govBusy} className="px-3 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">Cancel</button>
              <button onClick={confirmReject} disabled={!rejectReason.trim() || govBusy}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-500 rounded-lg disabled:opacity-40">Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
